import axios from 'axios';

const BASE_URL =
  process.env.REACT_APP_API_URL
  || (process.env.NODE_ENV === 'production'
    ? 'https://a.weave.md'
    : 'http://localhost:8000');
const allowed401s = ['/api/token/', '/api/token/refresh/']; // most 401s should take user to /login/. add valid 401s here

function redirectToLogin(autoLoggedOut) {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('currentUser');
  localStorage.removeItem('refreshingToken');
  const next = encodeURIComponent(window.location.pathname + window.location.search);
  const qs = autoLoggedOut ? `next=${next}&autologgedout=1` : `next=${next}`;
  window.location = `/login/?${qs}`;
}

// set interceptors to pass auth token if we have it in the local storage
axios.interceptors.request.use(
  config => {
    const token = localStorage.getItem('accessToken');
    if (token){
      config.headers.authorization = `Bearer ${token}`;
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// if response is 401 and it's not a retry, refresh access token; only send to login if refresh fails
axios.interceptors.response.use((response) => {
   return response
},
function (error) {
  const originalRequest = error.config;
  if (!originalRequest || !error.response || error.response.status !== 401) {
    return Promise.reject(error);
  }

  const path = originalRequest.url.replace(BASE_URL, '');
  if (allowed401s.includes(path) || originalRequest._retry) {
    return Promise.reject(error);
  }

  originalRequest._retry = true;
  const refresh = localStorage.getItem('refreshToken');
  if (!refresh) {
    redirectToLogin(true);
    return Promise.reject(error);
  }

  // Use raw axios so this does not re-enter postRequest's 401 handling
  return axios.post(BASE_URL + '/api/token/refresh/', { refresh })
    .then((response) => {
      const accessToken = response.data.access;
      localStorage.setItem('accessToken', accessToken);
      originalRequest.headers.authorization = `Bearer ${accessToken}`;
      return axios(originalRequest);
    })
    .catch(() => {
      redirectToLogin(true);
      return Promise.reject(error);
    });
})


const getRequest = (url, successCallback, errorCallback) => {
  return axios.get(BASE_URL + url)
    .then(response => {
        if (successCallback){
            return successCallback(response)
        }
    })
    .catch((error) => {
      // Safety net: interceptor should already have tried refresh + redirected
      if (error.response && error.response.status === 401 && !allowed401s.includes(url)){
        redirectToLogin(true);
      } else {
        if (errorCallback){
          return errorCallback(error)
        }
      }
    });
}

const postRequest = (url, data, successCallback, errorCallback, config) => {
  return axios.post(BASE_URL + url, data, config || undefined)
    .then((response) => {
        if (successCallback){
            return successCallback(response)
        }
    })
    .catch((error) => {
      if (error.response && error.response.status === 401 && !allowed401s.includes(url)){
        redirectToLogin(true);
      } else {
        if (errorCallback){
          return errorCallback(error)
        }
      }
    });
}

const patchRequest = (url, data, successCallback, errorCallback) => {
  return axios.patch(BASE_URL + url, data)
    .then((response) => {
      if (successCallback) {
        return successCallback(response);
      }
    })
    .catch((error) => {
      if (error.response && error.response.status === 401 && !allowed401s.includes(url)) {
        redirectToLogin(true);
      } else if (errorCallback) {
        return errorCallback(error);
      }
    });
};

const deleteRequest = (url, successCallback, errorCallback, data) => {
  return axios.delete(BASE_URL + url, data !== undefined ? { data } : undefined)
    .then((response) => {
      if (successCallback) {
        return successCallback(response);
      }
    })
    .catch((error) => {
      if (error.response && error.response.status === 401 && !allowed401s.includes(url)) {
        redirectToLogin(true);
      } else if (errorCallback) {
        return errorCallback(error);
      }
    });
};

function getApiErrorMessage(error, fallback = "Something went wrong.") {
  const status = error?.response?.status;
  if (!error?.response || status >= 500) {
    return "Something went wrong.";
  }
  const data = error.response.data;
  if (!data) return fallback;
  if (typeof data === "string") {
    if (data.startsWith("<") || data.includes("<html")) {
      return "Something went wrong.";
    }
    return data;
  }
  if (typeof data === "object") {
    const candidates = [data.error, data.detail, data.subdomain, data.name];
    for (const value of candidates) {
      if (Array.isArray(value) && value[0]) return String(value[0]);
      if (typeof value === "string" && value) return value;
    }
    const field = Object.keys(data).find((key) => data[key]);
    if (field) {
      const value = data[field];
      if (Array.isArray(value) && value[0]) return String(value[0]);
      if (typeof value === "string") return value;
    }
  }
  return fallback;
}

export {
  getRequest,
  postRequest,
  patchRequest,
  deleteRequest,
  getApiErrorMessage,
}
